<template>
    <nav ref="root"
         class="landlord-section-nav"
         :aria-label="navLabel"
         :class="{ 'is-stuck': isStuck }">
        <div ref="track" class="landlord-section-nav__track">
            <a v-for="s in sections"
               :key="s.id"
               :ref="'pill-' + s.id"
               :href="'#' + s.id"
               :aria-current="s.id === activeId ? 'true' : null"
               class="landlord-section-nav__pill"
               :class="{ 'is-active': s.id === activeId }"
               @click.prevent="onPillClick(s)">{{ s.label }}</a>
        </div>
        <button type="button"
                class="landlord-section-nav__top"
                :class="{ 'is-hidden': !showScrollTop }"
                :aria-label="topLabel"
                :title="topLabel"
                :tabindex="showScrollTop ? 0 : -1"
                :aria-hidden="showScrollTop ? null : 'true'"
                @click="scrollToTop">
            <i class="icon-arrow-up" aria-hidden="true"></i>
            <span class="sr-only">{{ topLabel }}</span>
        </button>
    </nav>
</template>

<script>
export default {
    name: 'LandlordPropertySectionNav',
    props: {
        sections:           { type: Array,  default: function () { return []; } },
        lang:               { type: String, default: 'nl' },
        scrollTopThreshold: { type: Number, default: 320 },
        offset:             { type: Number, default: 80 },
        // Opt in to the corrected landing offset. Off by default so the flows
        // that already sit right keep their current behaviour.
        anchorOffsetFix:    { type: Boolean, default: false },
    },
    data: function () {
        return {
            activeId:      null,
            isStuck:       false,
            showScrollTop: false,
            observer:      null,
            visibleRatios: {},
            lastSectionEl: null,
        };
    },
    watch: {
        activeId: function (newId) {
            this.applyActiveSectionClass(newId);
        },
    },
    computed: {
        topLabel: function () {
            return this.lang === 'en' ? 'Back to top' : 'Naar boven';
        },
        navLabel: function () {
            return this.lang === 'en' ? 'Property edit sections' : 'Secties woning bewerken';
        },
        reduceMotion: function () {
            if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
            return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        },
    },
    mounted: function () {
        this.boundScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.boundScroll, { passive: true });
        this.onScroll();
        var self = this;
        // Targets may render after this component mounts; defer observer setup one frame.
        this.$nextTick(function () {
            setTimeout(function () { self.setupObserver(); }, 0);
        });
        // Hash-on-load deep link from dashboard journey checklist + section autofocus.
        this.applyHashFromLocation();
    },
    beforeDestroy: function () {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        if (this.boundScroll) {
            window.removeEventListener('scroll', this.boundScroll);
        }
    },
    methods: {
        setupObserver: function () {
            if (typeof IntersectionObserver === 'undefined') return;
            var self = this;
            this.observer = new IntersectionObserver(function (entries) {
                entries.forEach(function (e) {
                    if (e.isIntersecting) {
                        self.visibleRatios[e.target.id] = e.intersectionRatio || 0.001;
                    } else {
                        delete self.visibleRatios[e.target.id];
                    }
                });
                var best = null, bestRatio = -1;
                self.sections.forEach(function (s) {
                    var r = self.visibleRatios[s.id];
                    if (typeof r !== 'undefined' && r > bestRatio) {
                        best = s.id;
                        bestRatio = r;
                    }
                });
                if (best && best !== self.activeId) {
                    self.activeId = best;
                    self.centerActivePill(best);
                }
            }, { rootMargin: '-90px 0px -55% 0px', threshold: [0, 0.2, 0.5, 1] });
            this.sections.forEach(function (s) {
                var el = document.getElementById(s.id);
                if (el) self.observer.observe(el);
            });
        },
        onScroll: function () {
            var root = this.$refs.root;
            if (root) {
                this.isStuck = root.getBoundingClientRect().top <= 0;
            }
            this.showScrollTop = (window.scrollY || window.pageYOffset || 0) > this.scrollTopThreshold;
        },
        // Used by both scroll paths: the pill click and the deep link on load.
        measuredOffset: function () {
            var root = this.$refs.root;
            var measured = root ? root.getBoundingClientRect().height : 0;
            if (this.anchorOffsetFix) {
                // The sticky nav already occupies the top of the viewport, so
                // reserving its height a second time overshoots the section.
                return Math.max(0, Math.round(measured) + 8 - 80);
            }
            return Math.max(this.offset, Math.round(measured) + 8);
        },
        onPillClick: function (section) {
            var el = document.getElementById(section.id);
            if (!el) return;
            var target = el.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0) - this.measuredOffset();
            var behavior = this.reduceMotion ? 'auto' : 'smooth';
            if (typeof window.scrollTo === 'function') {
                try { window.scrollTo({ top: target, behavior: behavior }); }
                catch (_) { window.scrollTo(0, target); }
            }
            this.activeId = section.id;
            this.centerActivePill(section.id);
        },
        centerActivePill: function (id) {
            var refKey = 'pill-' + id;
            var pillRefs = this.$refs[refKey];
            var pill = Array.isArray(pillRefs) ? pillRefs[0] : pillRefs;
            var track = this.$refs.track;
            if (!pill || !track) return;
            // Skip scroll if the pill is already comfortably in view — prevents micro-jitter
            // when active section flips between siblings during normal scrolling.
            var pillRect  = pill.getBoundingClientRect();
            var trackRect = track.getBoundingClientRect();
            var padding   = 24;
            var fullyVisible = pillRect.left >= trackRect.left + padding
                            && pillRect.right <= trackRect.right - padding;
            if (fullyVisible) return;
            if (typeof pill.scrollIntoView !== 'function') return;
            try {
                pill.scrollIntoView({
                    inline: 'center',
                    block:  'nearest',
                    behavior: this.reduceMotion ? 'auto' : 'smooth',
                });
            } catch (_) {
                // older browsers: silent fallback, no centering.
            }
        },
        applyHashFromLocation: function () {
            if (typeof window === 'undefined' || !window.location) return;
            var raw = (window.location.hash || '').replace(/^#/, '');
            if (!raw) return;
            var decoded = raw;
            try { decoded = decodeURIComponent(raw); } catch (e) { decoded = raw; }
            var self = this;
            var attempts = 0;
            var tryResolve = function () {
                var el = document.getElementById(decoded);
                if (!el) {
                    if (attempts++ < 4) {
                        setTimeout(tryResolve, 80);
                    }
                    return;
                }
                self.scrollToElement(el);
                self.focusFirstFieldIn(el);
                self.activeId = decoded;
                self.centerActivePill(decoded);
            };
            this.$nextTick(function () { setTimeout(tryResolve, 0); });
        },
        scrollToElement: function (el) {
            if (!el || typeof el.getBoundingClientRect !== 'function') return;
            var target = el.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0) - this.measuredOffset();
            var behavior = this.reduceMotion ? 'auto' : 'smooth';
            if (typeof window.scrollTo !== 'function') return;
            try { window.scrollTo({ top: target, behavior: behavior }); }
            catch (_) { window.scrollTo(0, target); }
        },
        focusFirstFieldIn: function (sectionEl) {
            if (!sectionEl || typeof sectionEl.querySelectorAll !== 'function') return;
            var candidates = sectionEl.querySelectorAll('input, textarea, select');
            for (var i = 0; i < candidates.length; i++) {
                var c = candidates[i];
                if (!c) continue;
                if (c.readOnly || c.disabled) continue;
                if (c.type === 'hidden') continue;
                var rect = c.getBoundingClientRect ? c.getBoundingClientRect() : null;
                if (rect && rect.width === 0 && rect.height === 0) continue;
                try {
                    if (typeof c.focus === 'function') {
                        c.focus({ preventScroll: true });
                    }
                } catch (_) { /* noop */ }
                return;
            }
        },
        scrollToTop: function () {
            var behavior = this.reduceMotion ? 'auto' : 'smooth';
            if (typeof window.scrollTo === 'function') {
                try { window.scrollTo({ top: 0, behavior: behavior }); }
                catch (_) { window.scrollTo(0, 0); }
            }
        },
        applyActiveSectionClass: function (sectionId) {
            // Defensive: section partials may not yet be rendered when activeId first
            // resolves. Find the anchor and walk up to the section wrapper.
            var anchor = sectionId ? document.getElementById(sectionId) : null;
            var section = anchor && anchor.closest ? anchor.closest('.create-property__section') : null;
            if (this.lastSectionEl && this.lastSectionEl !== section) {
                this.lastSectionEl.classList.remove('is-section-active');
            }
            if (section) {
                section.classList.add('is-section-active');
                this.lastSectionEl = section;
            } else {
                this.lastSectionEl = null;
            }
        },
    },
};
</script>
